import React from 'react';
import firebase from '../../utilities/firebase';
import Button from '@mui/material/Button';
import {
  Grid, IconButton, Paper, Chip, LinearProgress,
  Typography, Box,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { StateManager } from '../../utilities/stateManager.js';
import { Delete, DragIndicator, CheckCircle, RadioButtonUnchecked, ContentCopy, Refresh } from '@mui/icons-material';
import RequestManager from '../../utilities/requestManager';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const SPEC_FIELDS = [
  { key: 'year',              label: 'Year' },
  { key: 'make',              label: 'Make' },
  { key: 'model',             label: 'Model' },
  { key: 'trim',              label: 'Trim' },
  { key: 'color',             label: 'Ext. Color' },
  { key: 'interior-color',    label: 'Int. Color' },
  { key: 'miles',             label: 'Mileage' },
  { key: 'engine-type',       label: 'Engine' },
  { key: 'transmission',      label: 'Transmission' },
  { key: 'drivetrain',        label: 'Drivetrain' },
  { key: 'body-style',        label: 'Body Style' },
];

const MIN_BULLETS = 10;

// True if the global car modal is still showing this stock. Image analysis and
// writeup generation run in the background; by the time they finish the user may
// have navigated to a different car. We must never push a result into the UI of
// the car they're now looking at — only reflect it if they're still on this car.
const isViewingCar = (stock) =>
  new URLSearchParams(window.location.search).get('car') === String(stock);

export default function Writeup(props) {
  const { car } = props;

  const [newNote, setNewNote]           = React.useState('');
  const [bullets, setBullets]           = React.useState(car.bullets || []);
  const [writeupText, setWriteupText]   = React.useState(car.writeup || '');
  const [generating, setGenerating]     = React.useState(false);
  const [preppingWriteup, setPrepping]  = React.useState(false);
  const [savedAt, setSavedAt]           = React.useState(null);
  const [copied, setCopied]             = React.useState(false);

  // Auto-writeup run state. Old runs wrote only `ok` (no `status`), so fall back
  // to `ok` for the done/failed split when `status` is absent.
  const aw          = car.autoWriteup;
  const awRunning   = Boolean(aw && aw.status === 'running');
  const awFailed    = Boolean(aw && (aw.status === 'failed' || aw.ok === false));

  const missingCount  = SPEC_FIELDS.filter(f => !car[f.key] || String(car[f.key]).trim() === '').length;
  const isPrepped     = missingCount === 0;
  const bulletPct     = Math.min((bullets.length / MIN_BULLETS) * 100, 100);
  const wordCount     = writeupText.trim() ? writeupText.trim().split(/\s+/).length : 0;
  const hasWriteup    = writeupText.trim().length > 0;

  // One-click suggestions derived from boolean car flags already on the doc
  const suggestionMap = [
    { flag: 'has-air-conditioning', label: 'Air Conditioning' },
    { flag: 'has-power-brakes',     label: 'Power Brakes' },
    { flag: 'has-power-steering',   label: 'Power Steering' },
  ];
  if (car['interior-material']) {
    suggestionMap.push({ flag: '_always', label: car['interior-material'] + ' Interior' });
  }
  const suggestions = suggestionMap
    .filter(s => car[s.flag] || s.flag === '_always')
    .map(s => s.label)
    .filter(s => !bullets.some(b => b.toLowerCase().includes(s.toLowerCase())));

  // ── handlers ─────────────────────────────────────────────────────────────

  const prepForWriteup = () => {
    if (preppingWriteup) return;
    const stock = car.stock;
    setPrepping(true);
    StateManager.setAlertAndOpen("Prepping vehicle for writeup in the background...", "info");
    // The backend writes the extracted specs + bullets to Firestore itself, so the
    // data is safe regardless of where the user is. Only touch the UI if they're
    // still on this car — otherwise stay silent; they'll see it on their return.
    RequestManager.post({ function: "prepVehicleForWriteup", variables: { stock } })
      .then(async response => {
        if (!isViewingCar(stock)) return;
        setPrepping(false);
        if (response && response.success) {
          const doc = await firebase.firestore().doc('cars/' + stock).get();
          if (!isViewingCar(stock)) return;
          if (doc.exists) StateManager.updateCar(doc.data());
          if (Array.isArray(response.bullets) && response.bullets.length > 0) setBullets(response.bullets);
          StateManager.setAlertAndOpen("Vehicle prepped for writeup!", "success");
        }
      })
      .catch(() => {
        if (!isViewingCar(stock)) return;
        setPrepping(false);
        StateManager.setAlertAndOpen("Failed to prep vehicle for writeup", "error");
      });
  };

  const addNote = async (text) => {
    const note = (text !== undefined ? text : newNote).trim();
    if (!note) return;
    const parts = note.split(/[\n,]+/).map(b => b.trim()).filter(Boolean);
    const updated = [...bullets, ...parts];
    setBullets(updated);
    setNewNote("");
    StateManager.updateCar({ bullets: updated });
    await firebase.firestore().doc('cars/' + car.stock).update({ bullets: updated });
  };

  const deleteRow = async (index) => {
    const updated = bullets.filter((_, i) => i !== index);
    setBullets(updated);
    StateManager.updateCar({ bullets: updated });
    await firebase.firestore().doc('cars/' + car.stock).update({ bullets: updated });
  };

  const editBullet = async (index, value) => {
    const updated = bullets.map((b, i) => i === index ? value : b);
    setBullets(updated);
    StateManager.updateCar({ bullets: updated });
    await firebase.firestore().doc('cars/' + car.stock).update({ bullets: updated });
  };

  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const updated = Array.from(bullets);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);
    setBullets(updated);
    StateManager.updateCar({ bullets: updated });
    await firebase.firestore().doc('cars/' + car.stock).update({ bullets: updated });
  };

  const onWriteupChange = (e) => {
    const value = e.target.value;
    setWriteupText(value);
    StateManager.updateCar({ writeup: value });
    firebase.firestore().doc("cars/" + car.stock).set({ writeup: value, needsDAUpdate: true }, { merge: true });
    setSavedAt(new Date());
  };

  const generate = () => {
    if (generating) return;
    const stock = car.stock;
    setGenerating(true);
    StateManager.setAlertAndOpen("Generating writeup in the background...", "info");
    RequestManager.post({ function: "generateWriteup", variables: { stock } })
      .then(async response => {
        if (response && response.writeup) {
          // generateWriteup returns the text but does not persist it. Save it to
          // Firestore here so it's there when the user comes back, no matter which
          // car they're on now.
          await firebase.firestore().doc('cars/' + stock).set(
            { writeup: response.writeup, needsDAUpdate: true }, { merge: true }
          );
          if (!isViewingCar(stock)) return;
          setGenerating(false);
          setWriteupText(response.writeup);
          setSavedAt(new Date());
          StateManager.updateCar({ writeup: response.writeup });
          StateManager.setAlertAndOpen("Writeup generated!", "success");
        } else if (isViewingCar(stock)) {
          setGenerating(false);
        }
      })
      .catch(() => {
        if (!isViewingCar(stock)) return;
        setGenerating(false);
        StateManager.setAlertAndOpen("Failed to generate writeup", "error");
      });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(writeupText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* ── Auto-writeup result (from the backend pipeline that runs when a car
             enters the write-up stage). Read-only summary of the last run. ─── */}
      {aw && (
        <Paper
          variant="outlined"
          sx={{ p: 2, borderColor: awFailed ? 'error.main' : awRunning ? 'warning.main' : 'info.main' }}
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {awRunning ? 'Auto-writeup in progress…' : awFailed ? 'Auto-writeup failed' : 'Auto-writeup ran'}
            {aw.ranAt ? ` · ${awRunning ? 'started ' : ''}${aw.ranAt}` : ''}
          </Typography>

          {awRunning ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This car entered the write-up stage, so its specs, features, and writeup are being
                generated automatically. This usually takes a minute or two — the result will appear
                here when it finishes.
              </Typography>
              <LinearProgress />
            </>
          ) : awFailed ? (
            <Typography variant="body2" color="error.main">
              {aw.error || 'Unknown error — check the car notes.'}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {(aw.newFeaturesAdded || []).length} feature(s) added
                {(aw.newFeaturesAdded || []).length > 0
                  ? `: ${(aw.newFeaturesAdded || []).join(', ')}`
                  : ''}
              </Typography>
              {(aw.discrepancies || []).length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="warning.dark" fontWeight={600}>
                    Discrepancies to review:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {(aw.discrepancies || []).map((d, i) => (
                      <Typography component="li" variant="body2" color="warning.dark" key={i}>
                        {d}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>
      )}

      {/* ── Step 1: Data Prep ─────────────────────────────────────────── */}
      <StepCard
        number={1}
        title="Data Prep"
        status={isPrepped ? 'done' : preppingWriteup ? 'active' : 'pending'}
        statusLabel={isPrepped ? 'All specs filled' : `${missingCount} field${missingCount !== 1 ? 's' : ''} missing`}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Scan your uploaded photos to auto-fill missing specs and seed the features list.
        </Typography>

        <Grid container spacing={1} sx={{ mb: 2 }}>
          {SPEC_FIELDS.map(f => {
            const filled = Boolean(car[f.key] && String(car[f.key]).trim());
            return (
              <Grid item key={f.key}>
                <Chip
                  size="small"
                  icon={filled ? <CheckCircle /> : <RadioButtonUnchecked />}
                  label={f.label}
                  color={filled ? 'success' : 'default'}
                  variant={filled ? 'filled' : 'outlined'}
                />
              </Grid>
            );
          })}
        </Grid>

        {preppingWriteup && <LinearProgress sx={{ mb: 2 }} />}

        <Button
          variant="contained"
          color="secondary"
          onClick={prepForWriteup}
          disabled={preppingWriteup}
        >
          {preppingWriteup ? 'Analyzing Images…' : isPrepped ? 'Re-run Analysis' : 'Analyze Images & Fill Specs'}
        </Button>
      </StepCard>

      {/* ── Step 2: Feature Bullets ───────────────────────────────────── */}
      <StepCard
        number={2}
        title="Feature Bullets"
        status={bullets.length >= MIN_BULLETS ? 'done' : bullets.length > 0 ? 'active' : 'pending'}
        statusLabel={`${bullets.length} / ${MIN_BULLETS} features`}
      >
        {bullets.length < MIN_BULLETS && (
          <LinearProgress
            variant="determinate"
            value={bulletPct}
            sx={{ mb: 2, height: 6, borderRadius: 3 }}
          />
        )}

        {suggestions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Quick add from car data:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {suggestions.map(s => (
                <Chip
                  key={s}
                  label={`+ ${s}`}
                  size="small"
                  onClick={() => addNote(s)}
                  color="primary"
                  variant="outlined"
                  clickable
                />
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); }
            }}
            placeholder="Add feature — Enter to add, or paste a comma/newline-separated list"
            size="small"
            fullWidth
            multiline
            maxRows={6}
          />
          <Button variant="contained" onClick={() => addNote()} sx={{ whiteSpace: 'nowrap' }}>
            Add
          </Button>
        </Box>

        {bullets.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No features yet. Add at least {MIN_BULLETS} short highlights (2–5 words each), or run Step 1 to auto-generate them.
          </Typography>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="bullets">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {bullets.map((note, index) => (
                    <Draggable key={index} draggableId={String(index)} index={index}>
                      {(provided) => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          variant="outlined"
                          sx={{ mb: 0.5, px: 1, py: 0.25 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              {...provided.dragHandleProps}
                              sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', color: 'text.disabled' }}
                            >
                              <DragIndicator fontSize="small" />
                            </Box>
                            <TextField
                              value={note}
                              onChange={e => editBullet(index, e.target.value)}
                              fullWidth
                              variant="standard"
                              size="small"
                              InputProps={{ disableUnderline: true }}
                            />
                            <IconButton size="small" color="error" onClick={() => deleteRow(index)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Paper>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </StepCard>

      {/* ── Step 3: Writeup ───────────────────────────────────────────── */}
      <StepCard
        number={3}
        title="Writeup"
        status={hasWriteup ? 'done' : generating ? 'active' : 'pending'}
        statusLabel={hasWriteup ? `${wordCount} words` : 'Not generated'}
      >
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {!hasWriteup ? (
            <Button
              variant="contained"
              color="primary"
              onClick={generate}
              disabled={generating || bullets.length < 5}
            >
              {generating ? 'Generating…' : 'Generate Writeup'}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={generate}
                disabled={generating}
                size="small"
              >
                {generating ? 'Regenerating…' : 'Regenerate'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={copyToClipboard}
                data-sold-ok
                size="small"
                color={copied ? 'success' : 'primary'}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </>
          )}
          {savedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Saved {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
        </Box>

        {generating && <LinearProgress sx={{ mb: 2 }} />}

        {bullets.length < 5 && !hasWriteup && (
          <Typography variant="body2" color="warning.dark" sx={{ mb: 2 }}>
            Add at least 5 features in Step 2 to enable generation.
          </Typography>
        )}

        <TextField
          value={writeupText}
          onChange={onWriteupChange}
          multiline
          minRows={hasWriteup ? 20 : 6}
          fullWidth
          variant="outlined"
          placeholder="Generate a writeup above, or type one directly here…"
        />
      </StepCard>

    </Box>
  );
}

// ── StepCard ─────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  done:    { bg: '#e8f5e9', chip: '#2e7d32' },
  active:  { bg: '#e3f2fd', chip: '#1565c0' },
  pending: { bg: '#f5f5f5', chip: '#757575' },
};

function StepCard({ number, title, status, statusLabel, children }) {
  const { bg, chip } = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: bg,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 28, height: 28, borderRadius: '50%',
              bgcolor: chip, color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, flexShrink: 0,
            }}
          >
            {status === 'done' ? '✓' : number}
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        </Box>
        <Chip
          label={statusLabel}
          size="small"
          sx={{ bgcolor: chip, color: 'white', fontWeight: 600, fontSize: 11 }}
        />
      </Box>
      <Box sx={{ p: 2 }}>
        {children}
      </Box>
    </Paper>
  );
}
